import { Injectable, computed, signal } from '@angular/core';
import { ApiRecordStatus } from '../models/api.model';
import { AuthSession, User } from '../models/user.model';
import { UserRole } from '../models/user-role.enum';

const STORAGE_KEY = 'polilo.session';

/**
 * Owns the persisted session. Deliberately free of HTTP concerns so both the
 * auth service and the interceptor can depend on it without a cycle.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly session = signal<AuthSession | null>(this.restore());

  readonly user = computed<User | null>(() => this.session()?.user ?? null);
  readonly token = computed<string | null>(() => this.session()?.token ?? null);
  readonly refreshToken = computed<string | null>(() => this.session()?.refreshToken ?? null);
  readonly isAuthenticated = computed(() => this.session() !== null);
  readonly role = computed<UserRole | null>(() => this.session()?.user.role ?? null);

  set(session: AuthSession): void {
    this.session.set(this.normalize(session));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session()));
    } catch {
      /* storage unavailable (private mode) — session stays in memory only */
    }
  }

  /** Patch the signed-in user without touching tokens (e.g. after a profile save). */
  updateUser(user: User): void {
    const current = this.session();
    if (!current) {
      return;
    }
    this.set({ ...current, user });
  }

  clear(): void {
    this.session.set(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* no-op */
    }
  }

  hasRole(...roles: UserRole[]): boolean {
    const current = this.role();
    return current !== null && roles.includes(current);
  }

  private restore(): AuthSession | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return this.normalize(JSON.parse(raw) as AuthSession);
    } catch {
      return null;
    }
  }

  /** Older sessions may omit `status`; treat those as active. */
  private normalize(session: AuthSession): AuthSession {
    return {
      ...session,
      user: {
        ...session.user,
        status: session.user.status ?? ApiRecordStatus.ACTIVE,
      },
    };
  }
}
